import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Container,
  Alert,
} from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { remainingDays, formatJpDate } from "../../../../utils/dateUtils";
import InquiryButton from "../../../shared/InquiryButton";
import { handleApply } from "../../../utils/handleApply";
import { LevelTooltipText, levelMap } from "../../../../shared/Constants";

const JobDetailPage = ({ csrfToken }) => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [job, setJob] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [switchChecked, setSwitchChecked] = useState(false);

  // フラッシュメッセージ取得
  const location = useLocation();
  const flashMessage = location.state?.flashMessage;

  // textareaの改行設定
  const preLineLabels = [
    "備考",
    "概要",
    "スキル詳細",
    "工程詳細",
    "ポジション詳細",
  ];

  // 雇用形態を日本語名に変換するマップ
  const EMPLOYMENT_TYPE_LABELS = {
    full_time: "正社員",
    contract_employee: "契約社員",
    contractor: "業務委託",
    temporary: "派遣社員",
    part_time: "パート・アルバイト",
    other: "その他",
  };

  useEffect(() => {
    fetch(`/jobs/${id}.json`)
      .then((res) => res.json())
      .then((data) => {
        setJob(data);
        setSwitchChecked(data.published_flag);
      })
      .catch((e) => {
        console.error("求人の取得中にエラーが発生しました", e);
      });
  }, [id]);

  // 表示する行の定義
  const rows = useMemo(() => {
    if (!job) return [];

    return [
      { label: "求人名", value: job.name || "未設定" },
      { label: "概要", value: job.description || "未設定" },
      {
        label: "年収",
        value:
          job.price_min && job.price_max
            ? `${job.price_min}〜${job.price_max}万円`
            : "未設定",
      },
      {
        label: "作業場所",
        value:
          Array.isArray(job.prefecture_names) && job.prefecture_names.length > 0
            ? job.prefecture_names.join("、")
            : job.prefecture_names || "未設定",
      },
      {
        label: "雇用形態",
        value: job.employment_type
          ? EMPLOYMENT_TYPE_LABELS[job.employment_type] || "未設定"
          : "未設定",
      },

      {
        label: "入社日",
        value: job.joining_date
          ? `${formatJpDate(job.joining_date)} 以降`
          : "未設定",
      },
      {
        label: "スキル",
        value:
          Array.isArray(job.skills) && job.skills.length > 0
            ? job.skills.map((s) => {
                const year =
                  s.experience_year != null
                    ? `${s.experience_year}年以上`
                    : "年数未設定";
                const level =
                  s.level != null
                    ? `レベル${levelMap[s.level] || s.level}以上`
                    : "レベル未設定";
                return `${s.skill_name}（${year}、${level}）`;
              })
            : ["未設定"],
      },
      { label: "スキル詳細", value: job.skill_detail || "なし" },
      {
        label: "工程",
        value:
          Array.isArray(job.operations) && job.operations.length > 0
            ? job.operations
                .sort((a, b) => a.operation_id - b.operation_id)
                .map((p) => {
                  const year =
                    p.experience_year != null
                      ? `${p.experience_year}年以上`
                      : "年数未設定";
                  return `${p.operation_name}（${year}）`;
                })
            : ["未設定"],
      },
      { label: "工程詳細", value: job.operation_detail || "なし" },
      {
        label: "ポジション",
        value:
          Array.isArray(job.roles) && job.roles.length > 0
            ? job.roles.map((p) => {
                const year =
                  p.experience_year != null
                    ? `${p.experience_year}年以上`
                    : "年数未設定";
                return `${p.role_name}（${year}）`;
              })
            : ["未設定"],
      },
      { label: "ポジション詳細", value: job.role_detail || "なし" },
      {
        label: "面談回数",
        value: job.interview_count ? `${job.interview_count}` : "未設定",
      },
      { label: "備考", value: job.remark || "なし" },
    ];
  }, [job]);

  if (job === null) {
    return (
      <Box p={4}>
        <Typography>読み込み中...</Typography>
      </Box>
    );
  }

  return (
    <>
      {/* フラッシュメッセージ */}
      {flashMessage && (
        <Box sx={{ px: 2, py: 1 }}>
          <Alert severity="success">
            {typeof flashMessage === "object"
              ? flashMessage.message
              : flashMessage}
          </Alert>
        </Box>
      )}

      <Container maxWidth="md" sx={{ mt: 4, mb: 10 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={4}
        >
          {/* 左：戻るリンク */}
          <Typography
            sx={{
              fontSize: "1.25rem",
              fontWeight: "bold",
              textDecoration: "none",
              color: "inherit",
              cursor: "pointer",
              "&:hover": { opacity: 0.7 },
            }}
            onClick={() => navigate("/jobs")}
          >
            ＜ 戻る
          </Typography>

          {/* 右：詳細聞きたいボタン */}
          <Box
            textAlign="right"
            display="flex"
            flexDirection={isMobile ? "column" : "row"}
            alignItems="center"
            gap={1}
          >
            <InquiryButton
              isMobile={isMobile}
              onClick={() =>
                handleApply({
                  type: "jobs",
                  id: job.id,
                  csrfToken,
                })
              }
              label="詳細を聞きたい"
              tooltip="企業に問い合わせメールが送信されます"
              buttonSx={{
                background: "linear-gradient(to right, #2563eb, #7dd3fc)",
              }}
            />
          </Box>
        </Box>

        {/* 詳細本体エリア */}
        <Paper elevation={3} sx={{ borderRadius: 2, p: 4 }}>
          {/* 掲載終了日と残日数 */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
            sx={{
              flexDirection: { xs: "column", sm: "row" },
              gap: 1,
              alignItems: { xs: "flex-start", sm: "center" },
              textAlign: { xs: "left", sm: "inherit" },
            }}
          >
            <Typography fontSize="12px" color="gray">
              掲載期間 {formatJpDate(job.publish_end_date)}まで
            </Typography>
            <Typography fontSize="12px" color="text.secondary">
              {remainingDays(job.publish_end_date)}
            </Typography>
          </Box>

          {/* タイトル */}
          <Typography
            variant="h5"
            fontWeight="bold"
            mb={2}
            sx={{
              fontSize: { xs: "1rem", sm: "1.2rem" },
            }}
          >
            {job.title}
          </Typography>

          {/* 情報テーブル */}
          <Table size="small" sx={{ mb: 4, border: "1px solid #ccc" }}>
            <TableBody>
              {rows.map((row, index) =>
                isMobile ? (
                  <React.Fragment key={index}>
                    <TableRow>
                      <TableCell
                        colSpan={2}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "#4b5563" : "#9ca3af",
                          color: "white",
                          py: 1,
                          fontSize: "0.8rem",
                        }}
                      >
                        {row.label}
                        {row.label === "スキル" && (
                          <Box>
                            <LevelTooltipText
                              fontSize={10}
                              color="rgba(255, 255, 255, 0.8)"
                            />
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={2}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "#f3f4f6" : "#f9fafb",
                          fontSize: "0.8rem",
                          whiteSpace: preLineLabels.includes(row.label)
                            ? "pre-line"
                            : undefined,
                          wordBreak: preLineLabels.includes(row.label)
                            ? "break-all"
                            : undefined,
                        }}
                      >
                        {Array.isArray(row.value)
                          ? row.value.map((v, i) => <div key={i}>{v}</div>)
                          : row.value}
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ) : (
                  <TableRow key={index}>
                    <TableCell
                      align="right"
                      sx={{
                        backgroundColor:
                          index % 2 === 0 ? "#4b5563" : "#9ca3af",
                        color: "white",
                        width: "25%",
                      }}
                    >
                      {row.label}
                      {row.label === "スキル" && (
                        <Tooltip
                          title={<LevelTooltipText />}
                          arrow
                          placement="top"
                        >
                          <InfoOutlinedIcon
                            fontSize="small"
                            sx={{
                              ml: 0.5,
                              verticalAlign: "middle",
                              color: "white",
                              opacity: 0.7,
                              cursor: "pointer",
                            }}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor:
                          index % 2 === 0 ? "#f3f4f6" : "#f9fafb",
                        whiteSpace: preLineLabels.includes(row.label)
                          ? "pre-line"
                          : undefined,
                        wordBreak: preLineLabels.includes(row.label)
                          ? "break-all"
                          : undefined,
                      }}
                    >
                      {Array.isArray(row.value)
                        ? row.value.map((v, i) => <div key={i}>{v}</div>)
                        : row.value}
                    </TableCell>
                  </TableRow>
                )
              )}
            </TableBody>
          </Table>

          {/* 詳細聞きたいボタン */}
          <Box textAlign="center" mt={4}>
            <InquiryButton
              isMobile={isMobile}
              onClick={() =>
                handleApply({
                  type: "jobs",
                  id: job.id,
                  csrfToken,
                })
              }
              label="詳細を聞きたい"
              tooltip="企業に問い合わせメールが送信されます"
              buttonSx={{
                background: "linear-gradient(to right, #2563eb, #7dd3fc)",
              }}
            />
          </Box>
        </Paper>
      </Container>
    </>
  );
};

export default JobDetailPage;
